/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useContext, useMemo, useState } from "react";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { useGetBillingInfoQuery, useGetPricingInfoQuery } from "../../services/paymentService";
import { useLazyGetDoctorQuery } from "../../services/dataService";
import {
  useAddOrderMutation,
  useGetOrderPrescriptionQuery,
  useGetOrderQuery,
  useLazyGetOrderPrescriptionFormularyQuery,
  useLazyGetVoucherValidityQuery,
  useGetAccessTokenQuery,
  useLazyGetPreferencesQuery,
  useCreateCheckoutSessionMutation,
  useLazyGetLastOrderQuery,
  useLazyGetPreferenceImageQuery,
  useLazyGetSubscriptionUrlQuery,
  useCreateCheckoutSessionMedicationMutation
} from "../../services/orderService";
import { AppointmentContext } from "../../providers/AppointmentProvider";
import { RepeatOrderContext } from "../../providers/RepeatOrderProvider";
import { useModal } from "../../hooks/modalsHooks";
import useQuery from "../../hooks/useQuery";
import parseError from "../../utils/errorUtils";
import { getBase64 } from "../../utils/dataUtils";
import { getOrderName } from "../../utils/orderUtils";
import { useLoggerUtils } from "../../utils/loggerUtils";
import LoadingSpinner from "../../components/loadingSpinner/LoadingSpinner";
import {
  AppointmentCheckoutForm,
  AppointmentCheckoutFormFooter,
} from "./AppointmentCheckoutForm";
import GoBack from "../../components/GoBack";
import Collapsible from "../../components/collapsible/Collapsible";
import Avatar from "../../components/avatar/Avatar";
import Price from "../../components/price/Price";
import { Form, SubmitButton, TextField } from "../../components/forms";
import PreferenceSummary from "../../components/preferenceSummary/PreferenceSummary";
import "./appointment-page.scss";

const fonts = [
  {
    cssSrc:
      "https://fonts.googleapis.com/css2?family=Titillium+Web:wght@300;400;600&display=swap",
    family: "Titillium Web",
  },
];

function AppointmentPaymentsPage() {
  const { appointmentData, setAppointmentData } =
    useContext(AppointmentContext);
  const { isRepeatOrder, parentOrderId, hasSixMonthsPassed } =
    useContext(RepeatOrderContext);
  const { showModal, hideModal } = useModal();
  const query = useQuery();
  const orderType = query.get("type") ? query.get("type") : "other";
  const { id: orderId } = useParams();
  const { info } = useLoggerUtils();

  const navigate = useNavigate();

  const [initialValues, setInitialValues] = useState({
    name: "",
    terms_and_conditions: false,
  });
  const [couponId, setCouponId] = useState("");
  const [discount, setDiscount] = useState(0);
  const [doctorData, setDoctorData] = useState({});
  const [lastOrder, setLastOrder] = useState([]);
  const [lastOrderPrice, setLastOrderPrice] = useState(0);

  const [getDoctor] = useLazyGetDoctorQuery();
  const [getVoucherValidity] = useLazyGetVoucherValidityQuery();
  const [addOrder] = useAddOrderMutation();
  const [createCheckout] = useCreateCheckoutSessionMutation();
  const [createCheckoutSessionMedication] = useCreateCheckoutSessionMedicationMutation();
  const [getSubscriptionUrl] = useLazyGetSubscriptionUrlQuery();
  const { data: billingInfo } = useGetBillingInfoQuery();
  const { data: orderData } = useGetOrderQuery(
    { appointmentId: orderType === "REPEAT" ? orderId : parentOrderId },
    {
      skip: !(orderType === "REPEAT" ? orderId : parentOrderId),
      refetchOnMountOrArgChange: true,
    }
  );
  const { data: prescriptionData } = useGetOrderPrescriptionQuery(
    { orderId: orderType === "REPEAT" ? orderId : parentOrderId },
    {
      skip: !(orderType === "REPEAT" ? orderId : parentOrderId) || !orderType,
      refetchOnMountOrArgChange: true,
    }
  );
  const [repeatOrderData, setRepeatOrderData] = useState();
  const [getFormulary] = useLazyGetOrderPrescriptionFormularyQuery();
  const isChangeOrder = query.get("is_change_order");
  const accessToken = localStorage.getItem("AccessToken" || "");
  // Lazy query for preferences
  const [triggerGetPreferences, { data: preferences, isLoading }] =
    useLazyGetPreferencesQuery();
  const { data: subscriptionPricing, isSubscriptionLoading } = useGetPricingInfoQuery({
    type: "Subscription"
  });
  const [subscribed, setSubscribed] = useState(null);
  const [getLastOrder] = useLazyGetLastOrderQuery();
  const [getPreferenceImage] = useLazyGetPreferenceImageQuery();

  // Automatically fetch the token for get preferences
  const { data: tokenData } = useGetAccessTokenQuery();

  //calculate the total price
  useEffect(() => {
    if (prescriptionData) {
      setRepeatOrderData(prescriptionData);

      const tempPrice = prescriptionData?.medication?.reduce(
        (acc, item) => acc + (item?.charge || 0),
        0
      );
      setLastOrderPrice(tempPrice);
    }
  }, [prescriptionData]);

  //get last order details
  useEffect(async () => {
    try {
      if (parentOrderId) {
        const res = await getLastOrder({ orderId: parentOrderId });

        if (res.error) {
          console.error("Couldn't get last order : ", res.error);
        } else {
          let temp_arr = [];
          temp_arr.push(res.data);
          setLastOrder(temp_arr);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }, [parentOrderId, tokenData, getPreferenceImage]);

  //Fetch images for last order
  useEffect(() => {
    const fetchLastOrderImages = async () => {
      if (!tokenData) return;

      let tempArray = [];
      if (orderType === "REPEAT") {
        tempArray = isChangeOrder
          ? lastOrder
          : repeatOrderData?.medication || [];
      }

      if (tempArray.length > 0) {
        const updatedOrders = await Promise.all(
          tempArray.map(async (order) => {
            try {
              const res = await getPreferenceImage({
                sku: order.formulary?.stringId || order.stringId,
                preferenceToken: tokenData.data,
              });

              return {
                ...order,
                imageUrl:
                  res.data?.image_paths?.[0]?.image_url ||
                  "/images/medicine.png",
              };
            } catch (error) {
              console.error("Get image error:", error);
              return { ...order, imageUrl: "/images/medicine.png" };
            }
          })
        );

        if (orderType === "REPEAT") {
          if (isChangeOrder) {
            setLastOrder((prevOrders) =>
              JSON.stringify(prevOrders) !== JSON.stringify(updatedOrders)
                ? updatedOrders
                : prevOrders
            );
          } else {
            setRepeatOrderData((prevData) => {
              if (
                JSON.stringify(prevData?.medication) !==
                JSON.stringify(updatedOrders)
              ) {
                return { ...prevData, medication: updatedOrders };
              }
              return prevData;
            });
          }
        }
      }
    };

    if (
      (lastOrder?.length ?? 0) > 0 ||
      (repeatOrderData?.medication?.length ?? 0) > 0
    ) {
      fetchLastOrderImages();
    }
  }, [
    tokenData,
    orderType,
    isChangeOrder,
    lastOrder,
    repeatOrderData?.medication,
  ]);

  //Check for patient subscription status
  let tempAppointmentData = JSON.parse(localStorage.getItem("AppointmentData"));
  useEffect(() => {
    let stripeSubscription = tempAppointmentData?.patient?.stripeSubscription;
    if (!stripeSubscription) {
      setSubscribed(null);
    } else if (
      stripeSubscription &&
      stripeSubscription?.subscriptionStatus === "active" &&
      stripeSubscription?.lastPaymentStatus === "paid"
    ) {
      setSubscribed("paid");
    } else {
      setSubscribed("notPaid");
    }
  }, [tempAppointmentData]);

  // Fetch preferences only after the first API call is successful
  useEffect(() => {
    if (tokenData && isChangeOrder) {
      triggerGetPreferences({ preferenceToken: tokenData.data, accessToken });
    }
  }, [tokenData, triggerGetPreferences, isChangeOrder]);

  const handleGetDoctor = async () => {
    if (appointmentData?.doctorId) {
      try {
        const doctorRes = await getDoctor({
          doctorId: appointmentData.doctorId,
        });
        if (doctorRes.error) {
          // handle error
        } else {
          setDoctorData(doctorRes.data);
        }
      } catch (error) {}
    }
  };

  const handleCheckVoucherValidity = async (values) => {
    try {
      const res = await getVoucherValidity({ voucherId: values.voucher });

      if (res.error) {
        // handle error
        toast.error(parseError(res.error, "Couldn't submit voucher"));
        setCouponId(null);
      } else {
        // handle success
        if (res.data.valid) {
          setCouponId(values.voucher);
          const tempAppointmentPrice =
            orderType === "REPEAT"
              ? billingInfo?.repeatOrderCharge
              : isRepeatOrder
              ? billingInfo?.repeatAppointmentPrice
              : billingInfo?.appointmentPrice;

          const finalValue = tempAppointmentPrice * res.data.percentage;

          setDiscount(finalValue);
          toast.success("Voucher added successfully");
        } else {
          setCouponId(null);
          toast.error(
            parseError(
              res.error,
              "The discount code provided is not applicable or valid for use."
            )
          );
        }
      }
    } catch (error) {
      setCouponId(null);
      console.error(error);
    }
  };

  const handlePaymentDetailsProcess = async () => {
    const patient = orderData?.patient;

    let patientDetails = null;

    if (patient) {
      patientDetails = {
        firstName: patient.firstName,
        lastName: patient.lastName,
        phone: patient.phone,
        email: patient.email,
        dateOfBirth: patient.dateOfBirth,
      };
    }

    const appointmentSavedPictures =
      appointmentData?.pictures?.length > 0
        ? await Promise.all(
            appointmentData.pictures.map(async (doc) => {
              const fileURL = await getBase64(doc.file);
              return fileURL;
            })
          )
        : [];

    let data = {
      orderNotes: [],
      deliveryType: "STANDARD",
      paymentType: (orderType === "REPEAT" || orderType === "other") ? "ONLINE" : "VOUCHER",
      deviceType: "OTHER",
      info: {
        PRESENTING_COMPLAINT: appointmentData?.notes || "",
        COUPON: couponId,
      },
      ...(appointmentSavedPictures.length > 0
        ? { appointmentSavedPictures }
        : {}),
    };

    if (patientDetails) {
      data = {
        ...data,
        orderNotes: [
          {
            type: "REPEAT_ORDER_PATIENT_DETAIL_CONFIRMATION",
            questionAnswer: JSON.stringify(patientDetails),
          },
        ],
      };
    }

    if (orderType === "REPEAT") {
      const { formulary } = prescriptionData?.medication[0];

      try {
        const formularyRes = await getFormulary({
          formularyId: formulary?.id,
        });

        const gpFormulary = orderData.orderNotes.find(
          (note) => note.type === "GP_RECOMMENDATION"
        )?.formulary;

        let gpComment = "";

        if (gpFormulary) {
          gpComment = JSON.parse(gpFormulary)?.comments;
        }

        if (formularyRes.error) {
          // handle error
        } else {
          // handle success
          const prevPrescription = {
            medicationType: "Medical Cannabis",
            category: formularyRes.data.category,
            route: formularyRes.data.administrationRoute,
            manufacturer: formularyRes.data.manufacture,
            brand: formularyRes.data.brandName,
            strain: formularyRes.data.strain,
            comments: gpComment || "",
          };

          let repeatOrderNotes = [
            {
              type: "REPEAT_ORDER_RECOMMENDATION",
              formulary: JSON.stringify(prevPrescription),
            },
          ];

          if (appointmentData?.prescriptionRejectReason) {
            repeatOrderNotes.push({
              type: "REPEAT_ORDER_PATIENT_PRESCRIPTION_CHANGE_REASON",
              descriptionKey: appointmentData.prescriptionRejectReason.reason,
              description: appointmentData.prescriptionRejectReason.tellUsMore,
            });
          }

          if (appointmentData?.questionnaire) {
            repeatOrderNotes.push({
              type: "REPEAT_ORDER_QUESTIONNAIRE",
              questionAnswer: JSON.stringify(appointmentData?.questionnaire),
            });
          }

          if (appointmentData?.pegForm) {
            repeatOrderNotes.push(appointmentData?.pegForm);
          }

          if (appointmentData?.gadForm) {
            repeatOrderNotes.push(appointmentData?.gadForm);
          }

          if (
            appointmentData?.sideEffectForm &&
            appointmentData?.questionnaire.q_6.answer !== ""
          ) {
            repeatOrderNotes.push(appointmentData?.sideEffectForm);
          }

          data = {
            ...data,
            name: getOrderName(orderData.name, orderType),
            parentOrderItemId:
              hasSixMonthsPassed || isRepeatOrder
                ? Number(parentOrderId)
                : Number(orderId),
            isRepeatOrder: true,
            orderNotes: [...data.orderNotes, ...repeatOrderNotes],
            paymentMethodID:
              data.paymentMethodID && data.paymentMethodID !== ""
                ? data.paymentMethodID
                : "pm_1PZubJFtzggVtKWKysTLotpc",
          };

          localStorage.setItem("AddRepeatOrder", JSON.stringify(data));

          if (isChangeOrder) {
            const changeOrderData = {
              orderItemId: null,
              patientId: orderData?.patient?.id.toString(),
              preferences: preferences[0]?.preferences?.map((pref) => ({
                sku: pref?.sku,
                priority: pref?.priority?.toString(),
                name: pref?.name,
                imageURL: pref?.imageURL,
                itemQty: pref?.itemQty
              })),
            };
            localStorage.setItem("isChangeOrder", JSON.stringify(changeOrderData));
          }

          if (subscribed === null) {
            await handleCheckoutSession(data);
          } else if (subscribed === "paid") {
            setAppointmentData({});
            localStorage.setItem("subscribed", subscribed);
            let finalUrl = "";
            if(isChangeOrder){
              finalUrl = "/user/order?checkout=complete&is_change_order=true"
            }
            else{
              finalUrl = "/user/order?checkout=complete"
            }
            navigate(finalUrl, { replace: true });
          }
        }
      } catch (error) {
        console.error(error);
      }
    } else {
      let newOrderNotes = [];

      if (appointmentData?.prescriptionRejectReason) {
        newOrderNotes.push({
          type: "REPEAT_ORDER_PATIENT_PRESCRIPTION_CHANGE_REASON",
          descriptionKey: appointmentData.prescriptionRejectReason.reason,
          description: appointmentData.prescriptionRejectReason.tellUsMore,
        });
      }

      if (appointmentData?.questionnaire) {
        newOrderNotes.push({
          type: "REPEAT_ORDER_QUESTIONNAIRE",
          questionAnswer: JSON.stringify(appointmentData?.questionnaire),
        });
      }

      if (appointmentData?.pegForm) {
        newOrderNotes.push(appointmentData?.pegForm);
      }

      if (appointmentData?.gadForm) {
        newOrderNotes.push(appointmentData?.gadForm);
      }

      if (
        appointmentData?.sideEffectForm &&
        appointmentData?.questionnaire.q_6.answer !== ""
      ) {
        newOrderNotes.push(appointmentData?.sideEffectForm);
      }

      data = {
        ...data,
        name: getOrderName(appointmentData.name, orderType),
        type: appointmentData.type,
        appointment: {
          id: Number(orderId),
        },
        patient: appointmentData.patient,
        orderNotes: [...data.orderNotes, ...newOrderNotes],
        isRepeatOrder: hasSixMonthsPassed ? hasSixMonthsPassed : isRepeatOrder,
      };

      if (parentOrderId || isRepeatOrder) {
        data = {
          ...data,
          parentOrderItemId:
            hasSixMonthsPassed || isRepeatOrder
              ? Number(parentOrderId)
              : Number(orderId),
        };
      }

      if (subscribed === null) {
        localStorage.setItem("orderData", JSON.stringify(data));
        await handleCheckoutRepeatConsultation(data);
      } else if (subscribed === "paid") {
        localStorage.setItem("subscribed", subscribed);
        await handleAddOrder(data);
      }
    }
  };

  const handleAddOrder = async (orderData) => {
    try {
      let payload = orderData;
      if (orderData?.patient?.preferences?.TEMPORARY_APPOINTMENT) {
        payload = { ...orderData, source: "CALL_BOOKING" };
      }
      const res = await addOrder({ type: "BOOKING", values: payload });

      if (res.error) {
        // handle error
        toast.error(parseError(res.error, "Couldn't place order"));
      } else {
        //handle success
        setAppointmentData({});
        navigate(`/user/dashboard`, { replace: true });
        showModal({
          modalType: "SuccessModal",
          modalProps: {
            title: "Appointment successfully booked",
            body: (
              <>
                <p className="fw-light mb-1">
                  You’ve successfully booked your appointment on the
                  <b>
                    &nbsp;
                    {dayjs(appointmentData.datetime).format("MMMM Do, YYYY")}
                  </b>
                  &nbsp;at&nbsp;
                  <b>{dayjs(appointmentData.datetime).format("hh:mm A")}</b>
                  &nbsp;with
                  <b> {doctorData?.displayName}</b>.
                </p>
                <p className="mb-0">
                  Please check your spam folder if you haven’t received an email
                  confirmation.
                </p>

                <hr className="my-5 content-divider"/>

                <p className="fw-light mb-5">
                  Before you proceed with your video consultation,
                  please ensure you have a form of suitable photo-ID
                  ready to present on the video camera to confirm your
                  ID. 
                  <span className="mx-1">
                      <b>Acceptable forms of ID are your UK NIC card, driving license, passport, or
                      another valid form of Government-issued photo ID.</b>
                  </span>
                </p>
              </>
            ),
            width: "46rem",
            buttonText: "View my appointments",
            onButtonClick: () => {
              hideModal();
            },
          },
        });
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Request for a subscription payment link, if current subscription is failed.
  const handleSubscriptionFailed = async () => {
    try {
      let tempAppointmentData = JSON.parse(
        localStorage.getItem("AppointmentData")
      );
      let stripeSubscriptionId =
        tempAppointmentData?.patient?.stripeSubscriptionId;

      const res = await getSubscriptionUrl({
        id: stripeSubscriptionId,
        url: window.location.href,
      });

      if (res.error) {
        // handle error
        toast.error(
          parseError(res.error, "Couldn't generate subscription url")
        );
      } else {
        if (res?.data?.Subscription) {
          window.location.replace(res?.data?.Subscription?.cxPortalURL);
        } else {
          console.log("Something went wrong!");
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  //Pay as you go
  const handleCheckoutSession = async (orderData) => {
    let finalUrl = "";
    if (orderType === "REPEAT" && isChangeOrder) {
      finalUrl = window.location.protocol + "//" + window.location.host + "/user/order?checkout=complete&is_change_order=true"
    }
    else if (orderType === "REPEAT") {
      finalUrl = window.location.protocol + "//" + window.location.host + "/user/order?checkout=complete"
    } else {
      finalUrl = window.location.protocol + "//" + window.location.host + "/order/payment-success?isConsultation=true";
    }
    try {
      let data = {
        quantity: 1,
        customerId: appointmentData.patient.id.toString(),
        successUrl: finalUrl,
        cancelUrl: window.location.href,
        orderItemId: appointmentData.patient.id.toString(),
        description: orderType === "other" ? "Other" : "Repeat",
        paymentMode: "PAYMENT",
      };
      const res = await createCheckout({
        values: data,
      });

      if (res.error) {
        toast.error(parseError(res.error, "Stripe Checkout Error!"));
      } else {
        setAppointmentData({});
        if (res?.data?.sessionId) {
          localStorage.setItem("sessionId", res.data.sessionId);
        }
        window.location.replace(res?.data?.url);
      }
    } catch (error) {
      info("createCheckout - catch: " + JSON.stringify(error));
      console.error(error);
    }
  };

  const handleCheckoutRepeatConsultation = async (orderData) => {
    const finalUrl = window.location.protocol + "//" + window.location.host + "/order/payment-success?isConsultation=true";
    try {
      let data = {
        quantity: 1,
        customerId: appointmentData.patient.id.toString(),
        successUrl: finalUrl,
        cancelUrl: window.location.href,
        orderItemId: appointmentData.patient.id.toString(),
        description: orderType === "other" ? "Other" : "Repeat",
        paymentMode: "REPEAT_CONSULTATION",
      };
      const res = await createCheckoutSessionMedication({
        values: data,
      });

      if (res.error) {
        toast.error(parseError(res.error, "Stripe Checkout Error!"));
      } else {
        setAppointmentData({});
        if (res?.data?.sessionId) {
          localStorage.setItem("sessionId", res.data.sessionId);
        }
        window.location.replace(res?.data?.url);
      }
    } catch (error) {
      info("createCheckout - catch: " + JSON.stringify(error));
      console.error(error);
    }
  };

  useEffect(() => {
    setInitialValues((prevData) => ({
      ...prevData,
      name:
        orderType === "REPEAT"
          ? orderData?.patient?.displayName
          : appointmentData?.patient?.displayName,
    }));

    handleGetDoctor();
  }, [appointmentData]);

  //Change the format of delivery date
  const formatDeliveryDate = (isoString) => {
    if (isoString) {
      const date = new Date(isoString);
      return new Intl.DateTimeFormat("en-US", {
        month: "long",
        day: "numeric",
        year: "numeric",
      }).format(date);
    }
  };

  const stripePromise = useMemo(async () => {
    if (billingInfo) {
      return await loadStripe(billingInfo.publicKey);
    }

    return null;
  }, [billingInfo]);

  const appointmentPrice = useMemo(() => {
    const tempAppointmentPrice =
      orderType === "REPEAT"
        ? billingInfo?.repeatOrderCharge
        : isRepeatOrder
        ? billingInfo?.repeatAppointmentPrice
        : billingInfo?.appointmentPrice;

    if (discount > 0) {
      return tempAppointmentPrice - discount;
    }

    return tempAppointmentPrice;
  }, [billingInfo, discount, isRepeatOrder]);

  return (
    <Elements stripe={stripePromise} options={{ fonts }}>
      <div className="row mx-0 h-100">
        <div className="col-md-6 pt-5 position-relative bg-light-gray">
          <div className="row mx-0 mb-0 mb-md-8">
            <div className="col-12 col-lg-11 col-xl-9 col-xxl-8 mx-auto pb-5 pb-md-0 mb-0 mb-md-8">
              <GoBack />
              {orderType === "other" && (
                <div className="col-12 col-lg-12 col-xl-11 col-xxl-12 py-4 px-4 pb-4 pb-md-3 mb-0 mb-md-5 mt-5 bg-gray-card border-custom rounded">
                  <div className="mb-5">
                    <Collapsible title="Consultation Summary" expanded={true}>
                      <div className="text-primary fs-6 fw-bold">
                        Consultation details
                      </div>
                      <div className="d-flex align-items-center justify-content-between mt-4">
                        <div className="d-flex align-items-sm-center">
                          <div
                            className="d-none d-sm-block"
                            style={{
                              width: 56,
                              height: 56,
                            }}
                          >
                            <Avatar
                              src={
                                doctorData?.settings?.DOCTOR_AVATAR ||
                                "/images/samples/doctor-thumb.png"
                              }
                            />
                          </div>

                          <div className="ms-0 ms-sm-4">
                            <div className="primary-title fw-bold fs-5">
                              {doctorData?.displayName}
                            </div>
                            {appointmentData?.datetime && (
                              <div className="secondary-title text-dark-3 fw-light">
                                {dayjs(appointmentData.datetime).format(
                                  "DD/MM/YYYY"
                                )}
                                &nbsp; at&nbsp;
                                {dayjs(appointmentData.datetime).format("hh:mma")}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      {appointmentData?.notes && (
                        <>
                          <hr />
                          <div className="text-primary fs-6 fw-bold">
                            Consultation notes
                          </div>
                          <div className="d-flex align-items-center justify-content-between mt-2">
                            <div className="text-dark-4 pe-2">
                              {appointmentData.notes}
                            </div>
                          </div>
                        </>
                      )}
                    </Collapsible>
                  </div>
                </div>
              )}

              {orderType === "REPEAT" && !isChangeOrder && (
                <div className="d-flex flex-column align-items-center">
                  <h3 className="text-center fw-bold text-brown-black mt-4">
                    Order Overview
                  </h3>

                  <div className="text-center last-order-subheading">
                    Before finalizing, take a moment to review your selected
                    products and costs.
                  </div>

                  <div className="d-flex flex-column last-order-parent">
                    <div className="last-order-child-header">
                      <label className="text-primary fs-6 fw-bold last-order-label1">
                        Order items
                      </label>
                      <label className="text-primary fs-6 fw-bold last-order-label2">
                        {repeatOrderData?.medication?.length}
                      </label>
                    </div>

                    {repeatOrderData?.medication?.map((medicine) => (
                      <div
                        key={medicine.formulary.id}
                        className="last-order-child2 align-content-center"
                      >
                        <div>
                          <img
                            className="ms-2 last-order-img"
                            src={medicine.imageUrl}
                            alt={medicine.imageUrl}
                          />
                        </div>

                        <label className="align-content-center ms-3 medicine-text">
                          {medicine.formulary.productName}
                        </label>

                        {/* <div className="align-content-center medicine-quantity">
                                                Q{medicine.quantity}
                                            </div>

                                            <div className="align-content-center medicine-price">
                                                £{parseFloat(medicine.charge).toFixed(2)}
                                            </div> */}
                      </div>
                    ))}

                    {/* <div className="d-flex flex-row justify-content-between subtotal">
                                        <div className="align-content-center medicine-quantity">
                                            Subtotal
                                        </div>

                                        <div className="align-content-center medicine-quantity">
                                            £{parseFloat(lastOrderPrice).toFixed(2)}
                                        </div>
                                    </div> */}
                  </div>
                </div>
              )}

              {orderType === "REPEAT" && isChangeOrder && (
                <div className="d-flex flex-column">
                  <h3 className="text-center fw-bold text-brown-black mt-4">
                    Your order details
                  </h3>

                  <div className="d-flex justify-content-center">
                    <div className="text-center last-order-subheading">
                      Thank you for your order!. We appreciate your trust in us
                      and look forward to serving you!
                    </div>
                  </div>

                  {((isLoading) && isChangeOrder) && (
                    <div className="mt-3 mb-4 d-flex justify-content-center">
                      <LoadingSpinner />
                    </div>
                  )}
                  {(
                    (preferences) &&
                    (!isLoading)) &&
                    isChangeOrder && (
                      <PreferenceSummary
                        preferences={
                            preferences[0]?.preferences
                        }
                      />
                    )}

                  <div className="d-flex flex-column last-order-parent w-100">
                    <div className="last-order-child-header">
                      <label className="text-primary fs-6 fw-bold last-order-label1">
                        Your last order details
                      </label>
                      <label className="text-primary fs-6 fw-bold last-order-label2">
                        {formatDeliveryDate(orderData?.deliveryDate)}
                      </label>
                    </div>

                    {lastOrder?.map((order) => (
                      <div
                        key={order.stringId}
                        className="last-order-child2 align-items-center"
                      >
                        <div>
                          <img
                            className="ms-2 last-order-img"
                            src={order.imageUrl}
                            alt={order.imageUrl}
                          />
                        </div>

                        <label className="ms-3 medicine-text">
                          {order.productName}
                        </label>
                      </div>
                    ))}
                  </div>

                  {isChangeOrder && (
                    <div className="last-order-reason">
                      Reason to request a prescription
                    </div>
                  )}

                  {isChangeOrder && (
                    <div className="different-strain w-100">
                      <label>
                        {appointmentData?.prescriptionRejectReason?.reason}
                      </label>
                    </div>
                  )}

                  {isChangeOrder && (
                    <div className="text-left different-strain-text w-100">
                      As you wanted to change your medication, we have marked
                      your preferences as shown on the right side for the
                      doctor/clinician's consideration.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-md-6 pt-5 px-lg-6 mb-5 bg-white">
          <div className="row mx-0 mb-5 mb-md-0 pb-md-0">
            <>
              <div className="new-order-heading px-0">
                Payment details
              </div>
              <div className="ps-0 last-order-subheading">
                Your payment will not be charged until your prescription is approved.
              </div>
            </>
            {orderType !== "REPEAT" && (
              <div className="col-12 col-lg-12 col-xl-11 col-xxl-10 py-4 px-4 pb-4 pb-md-3 mb-0 mb-md-5 mt-4 bg-gray-card border-custom rounded">
                {!isChangeOrder && (
                  <div>
                    <Collapsible title="Payment Summary" expanded>
                      <div>
                        <div className="d-flex mb-3 align-items-center justify-content-between text-medium-gray fs-6">
                          <div>
                            {orderType === "REPEAT"
                              ? "Repeat prescription fee"
                              : "Consultation fee"}
                          </div>
                          <div>
                            <Price amount={appointmentPrice} currency="GBP" />
                          </div>
                        </div>
                        <hr />
                        <div className="d-flex align-items-center fw-bold justify-content-between text-primary fs-5">
                          <div>Total</div>
                          <div>
                            <Price amount={appointmentPrice} currency="GBP" />
                          </div>
                        </div>
                      </div>
                    </Collapsible>
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="row mx-0 mb-10 pb-md-0 scroll-content">
            {orderType === "REPEAT" && (
              <div className="px-0">
                {subscribed !== null && (
                  <h6 className="d-flex flex-row fw-bold gap-1 text-primary mb-2 mt-3 subscription-details">
                    Subscription details
                  </h6>
                )}

                {subscribed === null && (
                  <h6 className="d-flex flex-row fw-bold gap-1 text-primary mb-2 mt-4 subscription-details">
                    Payment method
                  </h6>
                )}

                <div className="subscription-card">
                  <div className="col subscription-card-col">
                    <div className="row align-items-center">
                      {subscribed !== null && (
                        <label className="subscription-card-heading">
                          Treat it clinic subscription
                        </label>
                      )}
                      {!subscribed && (
                        <label className="subscription-card-heading">
                          Treat it . Pay as You go
                        </label>
                      )}

                      {subscribed === "paid" && (
                        <div className="d-flex direction-row subscription-status-btn align-items-center">
                          <div className="subscription-status-icon"></div>
                          <label className="subscription-status">Active</label>
                        </div>
                      )}

                      {subscribed === "notPaid" && (
                        <div className="d-flex direction-row subscription-status-btn-inactive align-items-center">
                          <div className="subscription-status-icon-inactive"></div>
                          <label className="subscription-status-inactive">
                            Failed
                          </label>
                        </div>
                      )}
                    </div>

                    {subscribed !== null && (
                      <div className="row align-items-center">
                        <label className="subscription-price">£{subscriptionPricing?.amount || "14.5"}</label>
                        <label className="subscription-monthly">Monthly</label>
                      </div>
                    )}

                    {!subscribed && (
                      <div className="row align-items-center">
                        <label className="subscription-price">£9.99</label>
                        <label className="subscription-monthly">
                          Repeat prescription
                        </label>
                      </div>
                    )}

                    {subscribed === "paid" && (
                      <label className="subscription-text">
                        Your last payment was successful. You can go ahead and
                        place the order.
                      </label>
                    )}
                    {subscribed === null && (
                      <label className="subscription-text">
                        You can go ahead and place the order.
                      </label>
                    )}
                    {subscribed === "notPaid" && (
                      <label className="subscription-text-inactive">
                        Your last payment failed. Please try again, use a
                        different card or different payment method.
                      </label>
                    )}
                    {subscribed === "notPaid" && (
                      <button
                        className="update-subscription"
                        onClick={handleSubscriptionFailed}
                      >
                        Update subscription
                      </button>
                    )}
                  </div>
                </div>

                {orderType === "REPEAT" && subscribed !== "notPaid" && (
                  <div className="col-12 col-lg-12 col-xl-11 col-xxl-8 px-0">
                    <Form
                      initialValues={{
                        terms_and_conditions: "",
                      }}
                      onSubmit={handlePaymentDetailsProcess}
                    >
                      <AppointmentCheckoutFormFooter
                        orderType={orderType}
                        buttonText="Confirm Payment"
                      />
                    </Form>
                  </div>
                )}
              </div>
            )}
            {orderType !== "REPEAT" && (
              <div className="col-12 col-lg-12 col-xl-11 col-xxl-8 px-0">
                <Form
                  initialValues={{
                    terms_and_conditions: "",
                  }}
                  onSubmit={handlePaymentDetailsProcess}
                >
                  <AppointmentCheckoutFormFooter
                    orderType={orderType}
                    buttonText="Confirm Payment"
                  />
                </Form>
              </div>
            )}
            {orderType === "REPEAT" && isChangeOrder && (
              <div
                className="col-12 col-lg-12 col-xl-11 col-xxl-8 py-4 px-4 pb-5 pb-md-0 mb-0 mb-md-6
                        bg-gray-card border-custom rounded disclaimer-parent"
              >
                <h6 className="d-flex flex-row justify-content-center fw-bold gap-1 text-primary mb-2">
                  <img
                    src="/images/vectors/danger-primary.svg"
                    className="me-2 my-auto"
                    alt=""
                  />
                  Disclaimer
                </h6>
                <p className="text-dark-4 mb-4">
                  Your indicated preferences will be reviewed by the clinican
                  and a prescription will be issued considering your preferances
                  and stock availability at our partner pharmacies.
                </p>
              </div>
            )}
            {orderType === "REPEAT" && !isChangeOrder && (
              <div
                className="col-12 col-lg-12 col-xl-11 col-xxl-8 py-4 px-4 pb-5 pb-md-0 mb-0 mb-md-3
                                bg-gray-card border-custom rounded disclaimer-parent mt-6"
              >
                <h6 className="d-flex flex-row fw-bold gap-1 text-primary mb-2">
                  <img
                    src="/images/vectors/danger-primary.svg"
                    className="me-2 my-auto"
                    alt=""
                  />
                  Disclaimer
                </h6>
                <p className="text-dark-4 mb-4">
                  You will be charged for medications only after the
                  specialist approves your order.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Elements>
  );
}

// export function AppointmentCheckoutFormConsent() {
//     const { values } = useFormikContext();

//     return (
//         <>
//             <div className="mt-4">
//                 <Checkbox
//                     label="I am making this appointment for myself and not on behalf of another person, including children under the age of 18."
//                     name="identity_consent"
//                     className="check-lg"
//                     labelClass="ms-3 text-primary"
//                 />
//             </div>
//             <div className="mt-4">
//                 <Checkbox
//                     label={
//                         <p className="mb-0 fw text-primary">
//                             I have read and understood the&nbsp;
//                             <NavLink
//                                 className="fw-bold text-primary text-decoration-underline"
//                                 to="/terms-condition"
//                                 target="_blank"
//                                 rel="noopener noreferrer"
//                             >
//                                 Terms and Conditions
//                             </NavLink>
//                             &nbsp;prior to booking a consultation
//                         </p>
//                     }
//                     tooltip="More Details"
//                     name="terms_and_conditions"
//                     className="check-lg"
//                     labelClass="ms-3 text-primary"
//                 />
//             </div>
//             <div className="row mx-0 mt-6 mb-4">
//                 <SubmitButton
//                     disabled={
//                         !values.terms_and_conditions || !values.identity_consent
//                     }
//                 >
//                     Confirm Payment
//                 </SubmitButton>
//             </div>
//         </>
//     );
// }

export default AppointmentPaymentsPage;
